/**
 * TanStack Query hooks for the Backup board.
 *
 * Cadence, honestly matched to the data (dashboard design rules §5 — never
 * poll faster than the number can change):
 *
 * - **Veeam** arrives by push from the on-premises collector every **15
 *   minutes**. Refetching faster just re-serves the same rows.
 * - **Azure Backup** arrives by pull on NOVA's existing Resource Graph poller.
 *
 * So the board polls on the collector's own beat, with `staleTime` just under
 * it so a tab switch does not fire a pointless request. The one exception is
 * the L0 overview: it carries the *collector liveness* that turns the tiles to
 * unknown, and that transition should not wait a quarter of an hour — it
 * refetches at 5 minutes.
 *
 * Site rollups and the coverage join move slower still, so they refetch at 30
 * minutes.
 *
 * None of these endpoints throw when a source is unconfigured or the collector
 * has gone quiet: they answer 200 with `configured` / `status_detail` /
 * `errors` / `stale` set, and the board explains the gap. A thrown error here
 * means the *NOVA* API is down, not Veeam.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type BackupAnnotationCreate,
  createBackupJobAnnotation,
  getBackupActions,
  getBackupCoverage,
  getBackupJobRuns,
  getBackupJobs,
  getBackupOverview,
  getBackupSites,
  getBackupSlaCompliance,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Shared prefix, so one invalidation drops the whole board. */
const BACKUP_KEY = ["backup"] as const;

/** Collector liveness — the tiles flip to unknown off the back of this. */
const LIVENESS_REFETCH_MS = 5 * 60_000;

/** The collector's own cadence: nothing below this can have changed. */
const COLLECTOR_REFETCH_MS = 15 * 60_000;

/** Daily rollups and the inventory join — slower than the collector beat. */
const SLOW_REFETCH_MS = 30 * 60_000;

/** Just under the collector beat, so a tab switch does not refetch for nothing. */
const COLLECTOR_STALE_MS = 14 * 60_000;

export function useBackupOverview() {
  return useQuery({
    queryKey: [...BACKUP_KEY, "overview"],
    queryFn: async () => {
      const { data } = await getBackupOverview();
      logger.debug("[backup] overview received", {
        configured: data?.configured,
        stale: data?.stale,
        succeeded: data?.succeeded,
        failed: data?.failed,
        didNotRun: data?.did_not_run,
        collectors: data?.collectors.length,
      });
      return data;
    },
    refetchInterval: LIVENESS_REFETCH_MS,
  });
}

/**
 * The whole job list, unfiltered.
 *
 * The endpoint accepts single-value `site` / `sla` / `result` / `source`
 * filters, but the board's `FilterSelect`s are multi-select and several panels
 * derive their own aggregates (the SLA tiers) from the same rows. Fetching once
 * and slicing in the browser keeps every panel agreeing on one snapshot — and
 * the estate is ~50 jobs, so there is nothing to save by pushing it down.
 */
export function useBackupJobs() {
  return useQuery({
    queryKey: [...BACKUP_KEY, "jobs"],
    queryFn: async () => {
      const { data } = await getBackupJobs();
      logger.debug("[backup] jobs received", { total: data?.total, errors: data?.errors.length });
      return data;
    },
    refetchInterval: COLLECTOR_REFETCH_MS,
    staleTime: COLLECTOR_STALE_MS,
  });
}

/** Per-site, per-day rollups for the heat strip. */
export function useBackupSites(days = 30) {
  return useQuery({
    queryKey: [...BACKUP_KEY, "sites", days],
    queryFn: async () => {
      const { data } = await getBackupSites({ query: { days } });
      logger.debug("[backup] sites received", { days: data?.days, sites: data?.sites.length });
      return data;
    },
    refetchInterval: SLOW_REFETCH_MS,
  });
}

/**
 * The rolling success rate per site and per SLA tier.
 *
 * A thirty-day aggregate moves about as fast as a daily rollup does, so it sits
 * on the slow beat with the site strip rather than the collector's fifteen
 * minutes — one more night's sessions cannot visibly shift a month's rate.
 */
export function useBackupSlaCompliance(days = 30) {
  return useQuery({
    queryKey: [...BACKUP_KEY, "sla", days],
    queryFn: async () => {
      const { data } = await getBackupSlaCompliance({ query: { days } });
      logger.debug("[backup] sla compliance received", {
        days: data?.days,
        target: data?.target_pct,
        readable: data?.readable,
        sites: data?.sites.length,
        tiers: data?.tiers.length,
      });
      return data;
    },
    refetchInterval: SLOW_REFETCH_MS,
  });
}

/**
 * The board's task list. Polled on the collector beat rather than the slow one:
 * it is the panel somebody actually watches, and a task that arrived four
 * minutes ago should not wait half an hour to appear.
 */
export function useBackupActions() {
  return useQuery({
    queryKey: [...BACKUP_KEY, "actions"],
    queryFn: async () => {
      const { data } = await getBackupActions();
      logger.debug("[backup] actions received", {
        now: data?.now,
        soon: data?.soon,
        backlog: data?.backlog,
        stale: data?.stale,
      });
      return data;
    },
    staleTime: COLLECTOR_STALE_MS,
    refetchInterval: COLLECTOR_REFETCH_MS,
  });
}

export function useBackupCoverage() {
  return useQuery({
    queryKey: [...BACKUP_KEY, "coverage"],
    queryFn: async () => {
      const { data } = await getBackupCoverage();
      logger.debug("[backup] coverage received", {
        servers: data?.servers_total,
        unprotected: data?.protected_none,
        orphaned: data?.orphaned,
        inventoryStale: data?.inventory_stale,
      });
      return data;
    },
    refetchInterval: SLOW_REFETCH_MS,
  });
}

/**
 * One job's run history with its acknowledgements attached (L2). Disabled
 * until a job is actually selected, so opening the board costs nothing.
 */
export function useBackupJobRuns(name: string | null, source: string | null, days = 90) {
  return useQuery({
    queryKey: [...BACKUP_KEY, "job-runs", name, source, days],
    queryFn: async () => {
      const { data } = await getBackupJobRuns({
        path: { name: name as string },
        query: { source, days },
      });
      logger.debug("[backup] job runs received", {
        name,
        runs: data?.runs.length,
        annotations: data?.annotations.length,
        truncated: data?.truncated,
      });
      return data;
    },
    enabled: name != null,
    refetchInterval: COLLECTOR_REFETCH_MS,
  });
}

/**
 * Acknowledge a failure: the comment/resolution pair that replaces the
 * spreadsheet's `Failure Comments` / `Solution` columns (concept §5).
 *
 * The author is taken from the signed-in session on the server and is never
 * accepted from this request — that is the difference between an audit trail
 * and a text field.
 */
export function useCreateBackupAnnotation(name: string | null, source: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: Omit<BackupAnnotationCreate, "source">) => {
      const { data } = await createBackupJobAnnotation({
        path: { name: name as string },
        body: { ...body, ...(source ? { source } : {}) },
      });
      return data;
    },
    onSuccess: () => {
      logger.debug("[backup] annotation saved", { name, source });
      // The detail view gains an annotation; the job list gains a count.
      void queryClient.invalidateQueries({ queryKey: [...BACKUP_KEY, "job-runs", name, source] });
      void queryClient.invalidateQueries({ queryKey: [...BACKUP_KEY, "jobs"] });
    },
  });
}
